import { DEV_SSE_EVENT_NAME } from '@/utilities/helpers/eventStreamDevTools';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { endsWith, isEmpty, startsWith, trim } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SSE } from 'sse.js';

const buildUrl = (path, pathParams = {}, queryParams = {}) => {
  const isDefaultPort = (port) => ['80', '443'].includes(port);

  const buildPathParams = (path, pathParams = {}) => {
    if (isEmpty(pathParams)) return path;

    Object.entries(pathParams).forEach(([key, value]) => {
      path = path.replace(`{${key}}`, encodeURIComponent(value));
    });

    return path;
  };

  const buildQueryParams = (path, queryParams = {}) => {
    if (isEmpty(queryParams)) return path;

    return (
      path +
      '?' +
      Object.entries(queryParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')
    );
  };

  const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars);
  const API_URL = ENV_VARS.API_URL;
  const API_PORT = ENV_VARS.API_PORT;
  const basePath = `${API_URL}${isDefaultPort(API_PORT) ? '' : ':' + API_PORT}`;
  path = buildPathParams(path, pathParams);
  path = buildQueryParams(path, queryParams);
  return basePath + path;
};

export const useEventStream = (userInfo, eventTypes, principalUserUuids, options = {}) => {
  const { enabled = true } = options;
  const [latestEvent, setLatestEvent] = useState({});
  const [eventStream, setEventStream] = useState([]);
  const { token = '' } = useSelector(getCurrentUser);

  // Serialize array deps to stable strings so the effect only re-fires
  // when the actual content changes, not on every render's new array reference.
  const eventTypesKey = JSON.stringify(eventTypes);
  const principalUuidsKey = JSON.stringify(principalUserUuids);

  useEffect(() => {
    if (!enabled) return;

    if (isEmpty(token) || isEmpty(userInfo.useruuid) || isEmpty(eventTypes)) return;

    if (principalUserUuids && isEmpty(principalUserUuids)) {
      return;
    }

    const url = buildUrl('/hacloud/api/v1/stream/user_events/{useruuid}', {
      useruuid: userInfo.useruuid,
    });

    const source = new SSE(url, {
      method: 'POST',
      payload: JSON.stringify({
        message_type: [...eventTypes],
        ...(principalUserUuids?.length && { useruuid: principalUserUuids }),
      }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      start: false,
      autoReconnect: true,
      reconnectDelay: 3000,
      // Retry indefinitely so transient outages (sleep/wake, Wi-Fi switch, backend
      // restart) self-heal; the 503 handler below throttles reconnects on capacity limits.
      maxRetries: null,
      useLastEventId: true,
    });

    // Timer for the delayed reconnect after a capacity (503) rejection.
    let capacityRetryTimer = null;

    const processIncomingEvent = (event) => {
      if (!event || typeof event !== 'object') {
        return;
      }

      if (!eventTypes.includes(event.message_type)) {
        return;
      }

      setLatestEvent(event);
      setEventStream((es) => [...es, event]);
    };

    source.addEventListener('open', () => {
      if (source.lastEventId) {
        console.warn(
          `useEventStream: Event stream reconnected, resuming from event ${source.lastEventId}`
        );
      }
    });

    source.addEventListener('error', (e) => {
      console.error('useEventStream: Error occurred in event stream: ', e);

      // 503 = server live-stream session pool is full (MAX_SESSIONS). Pause sse.js's fast
      // auto-reconnect and retry after a delay; the backend heartbeat reaps dead sessions
      // (~60s), so capacity frees up. Cross-origin XHR can't read Retry-After (not
      // CORS-exposed), so use a fixed delay with jitter.
      if (e?.responseCode === 503) {
        source.autoReconnect = false;
        const delayMs = 30000 + Math.floor(Math.random() * 5000);
        console.warn(
          `useEventStream: Live stream capacity reached (503); retrying in ${delayMs}ms.`
        );
        clearTimeout(capacityRetryTimer);
        capacityRetryTimer = setTimeout(() => {
          source.autoReconnect = true;
          source.stream();
        }, delayMs);
        return;
      }

      if (source.maxRetries && source.retryCount >= source.maxRetries) {
        console.warn('useEventStream: Max retries reached, connection permanently closed.');
      } else if (source.autoReconnect) {
        console.warn(`useEventStream: Connection lost, will retry in ${source.reconnectDelay}ms`);
        console.warn(
          `useEventStream: Retry attempt ${source.retryCount + 1}${
            source.maxRetries ? '/' + source.maxRetries : ''
          }`
        );
      }
    });

    source.addEventListener('abort', (e) => {
      if (source.lastEventId) {
        console.warn(
          `useEventStream: Event stream has been aborted for event ${source.lastEventId}. `,
          e
        );
      } else {
        console.warn('useEventStream: Event stream has been aborted. ', e);
      }
    });

    source.addEventListener('message', (e) => {
      const eventString = trim(e.data);
      if (!isEmpty(eventString) && startsWith(eventString, '{') && endsWith(eventString, '}')) {
        try {
          const event = JSON.parse(eventString);
          processIncomingEvent(event);
        } catch (error) {
          console.warn('useEventStream: Failed to parse event payload.', error);
        }
      }
    });

    const handleDevStubEvent = (event) => {
      processIncomingEvent(event.detail);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(DEV_SSE_EVENT_NAME, handleDevStubEvent);
    }

    source.stream();

    return () => {
      clearTimeout(capacityRetryTimer);
      source.close();

      if (typeof window !== 'undefined') {
        window.removeEventListener(DEV_SSE_EVENT_NAME, handleDevStubEvent);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token, userInfo.useruuid, eventTypesKey, principalUuidsKey]);

  return { latestEvent, eventStream };
};
